import moment from 'moment';
import { parseMediaFiles } from '@/general/converters/mediaFileConverters';
import Severity from '@/shared/constants/Severity';
import { BASE_SCHEMA } from '@/shared/converters/baseConverters';
import { parseEntities, parseEntity } from '@/shared/converters/commonConverters';
import { DateTimeFormat, formatMoment, parseMoment } from '@/shared/converters/dateConverters';
import User from '@/shared/models/User';
import AxleLocation from '../constants/AxleLocation';
import BrakeType from '../constants/BrakeType';
import DimensionMask from '../constants/DimensionMask';
import Axle, { AxleId } from '../models/Axle';
import AxlesData from '../models/AxleFormData';
import Brake from '../models/Brake';
import Measurement from '../models/Measurement';
import Threshold from '../models/Threshold';
import Tire from '../models/Tire';
import TireContainer from '../models/TireContainer';
import TireMake from '../models/TireMake';
import TireNote from '../models/TireNote';
import TirePosition from '../models/TirePosition';

export interface AxleData {
  id: AxleId | null;
  location: AxleLocation;
  index: number;
  wheelCount: number;
  brakeType: BrakeType;
}

enum ApiMeasurementType {
    TREAD_DEPTH = 'TREAD_DEPTH',
    BRAKE_DISC = 'BRAKE_DISC',
    BRAKE_PAD = 'BRAKE_PAD',
}

const CONTAINER_SCHEMA = {
  vehicleId: 'dealerObjectId',
  repairKitExpiryDate: (data: any) => parseMoment(data.tireRepairKitExpiryDate, DateTimeFormat.PLANNER_DATE),
  storageLocation: (data: any) => data.tireStorageLocation || null,
  axles: (data: any) => parseAxles(data.wheels),
  thresholds: (data: any) => parseThresholds(data.thresholds),
};

const AXLE_SCHEMA = {
  id: 'axleId',
  location: 'axlePosition',
  index: 'axleIndex',
  brakeDiscMinWidth: 'brakeDiscMinWidth',
  brakeType: 'brakeType',
  positions: (data: any) => parsePositions(data.positions),
};

const THRESHOLD_SCHEMA = {
  type: 'type',
  caution: 'caution',
  bad: 'alert',
};

const POSITION_SCHEMA = {
  id: 'id',
  index: 'index',
  tires: (data: any) => parseTires(data.tires),
  brakeMedia: (data: any) => parseMediaFiles(data.brakeMedia),
  brake: (data: any) => parseBrake(data),
};

const BRAKE_SCHEMA = {
  discMeasurement: (data: any) => parseMeasurement(data.brakeMeasurements?.disc),
  padMeasurement: (data: any) => parseMeasurement(data.brakeMeasurements?.pad),
  note: (data: any) => parseNote(data.brakeNote),
};

const MEASUREMENT_SCHEMA = {
  value: 'value',
  severity: (data: any) => parseSeverity(data.severity),
};

const NOTE_SCHEMA = {
  id: 'id',
  content: 'content',
  modifiedOn: (data: any) => parseMoment(data.modifiedOn),
};

const TIRE_SCHEMA = {
  ...BASE_SCHEMA,
  isMounted: 'isMounted',
  isNew: 'isNew',
  season: 'season',
  rimType: 'rimType',
  isRunFlat: 'isRunFlat',
  isHomologated: 'isHomologated',
  hasNoiseReduction: 'hasNoiseReduction',
  isPressureCorrected: 'isPressureCorrected',
  isPressureSensorReset: 'isPressureSensorReset',
  ean: 'EAN',
  make: (data: any) => TireMake.withData({ id: data.brandId, description: data.brand }),
  model: 'model',
  dimension: (data: any) => data.dimension || null,
  dimensionMask: (data: any) => parseDimensionMask(data.dimensionMask),
  productionWeek: 'productionWeek',
  media: (data: any) => parseMediaFiles(data.brakeMedia),
  measurement: (data: any) => parseTireMeasurement(data),
  note: (data: any) => parseNote(data.note),
};

export function parseTireContainer(data: any): TireContainer | null {
  const container = parseEntity(data, CONTAINER_SCHEMA, TireContainer);
  container?.setRepairKitStatus({
    present: data.hasTireRepairKit,
    expiryDate: container?.repairKitExpiryDate,
  });
  return container;
}

export function parseAxles(data: any): Array<Axle> {
  return parseEntities(data, AXLE_SCHEMA, Axle);
}

export function parseThresholds(data: any): Array<Threshold> {
  return parseEntities(data, THRESHOLD_SCHEMA, Threshold);
}

export function parsePositions(data: any): Array<TirePosition> {
  return parseEntities(data, POSITION_SCHEMA, TirePosition);
}

export function parseTires(data: any): Array<Tire> {
  return parseEntities(data, TIRE_SCHEMA, Tire);
}

export function parseBrake(data: any): Brake | null {
  return parseEntity(data, BRAKE_SCHEMA, Brake);
}

export function parseTireMeasurement(data: any): Measurement | null {
  const measurement = data?.measurement;
  if (measurement && data.isMounted && !measurement.isFromDossier) return null;
  return parseEntity(measurement, MEASUREMENT_SCHEMA, Measurement);
}

export function parseMeasurement(data: any): Measurement | null {
  return parseEntity(data, MEASUREMENT_SCHEMA, Measurement);
}

export function parseNote(data: any): TireNote | null {
  return parseEntity(data, NOTE_SCHEMA, TireNote);
}

function parseDimensionMask(mask: string | null): DimensionMask | null {
  switch (mask) {
    case 'R':
      return DimensionMask.RADIAL;
    case 'ZR':
      return DimensionMask.PERFORMANCE;
    case 'DL':
      return DimensionMask.DOUBLE_LOAD;
    default:
      return null;
  }
}

export function formatTire(tire: Tire): any {
  return {
    ...tire,
    dimensionMask: formatDimensionMask(tire.dimensionMask),
    brandId: tire.make?.id ?? null,
    EAN: tire.ean,
  };
}

function formatDimensionMask(dimensionMask: DimensionMask | null): string | null {
  switch (dimensionMask) {
    case DimensionMask.RADIAL:
      return 'R';
    case DimensionMask.PERFORMANCE:
      return 'ZR';
    case DimensionMask.DOUBLE_LOAD:
      return 'DL';
    default:
      return null;
  }
}

export function formatMeasurementData(data: AxlesData, modifiedBy: User): any {
  return {
    storageLocation: data.storageLocation,
    brakeDiscMinWidth: Object.entries(data.brakeDiscMinWidths).map(entry => {
      return {
        axleId: Number(entry[0]),
        minWidth: entry[1],
      };
    }),
    measurements: Object.entries(data.measurements).flatMap(entry => {
      const positionId = Number(entry[0]);
      const measurement = entry[1];
      return [
        formatMeasurement(positionId, measurement.mountedTire, ApiMeasurementType.TREAD_DEPTH, true, modifiedBy),
        measurement.storedTire ? formatMeasurement(positionId, measurement.storedTire, ApiMeasurementType.TREAD_DEPTH, false, modifiedBy) : null,
        measurement.disc ? formatMeasurement(positionId, measurement.disc, ApiMeasurementType.BRAKE_DISC, true, modifiedBy) : null,
        measurement.pad ? formatMeasurement(positionId, measurement.pad, ApiMeasurementType.BRAKE_PAD, true, modifiedBy) : null,
      ];
    }).filter(Boolean),
  };
}

function formatMeasurement(positionId: number, measurement: Measurement, type: ApiMeasurementType, isMounted: boolean, modifiedBy: User): any {
  return {
    positionId,
    isMounted,
    type,
    value: measurement.value ?? null,
    severity: formatSeverity(measurement.severity) ?? null,
    modifiedById: modifiedBy?.id ?? null,
    modifiedOn: formatMoment(moment()),
  };
}

export function formatAxleLayout(data: Array<AxleData>): any {
  return data.map((axle) => {
    return {
      axlePosition: axle.location,
      order: axle.index,
      wheelCount: axle.location === AxleLocation.SPARE ? 1 : axle.wheelCount,
      brakeType: axle.location === AxleLocation.SPARE ? BrakeType.NO_BRAKES : axle.brakeType,
    };
  });
}

function parseSeverity(severity: string | null): Severity | null {
  switch (severity) {
    case 'NOTICE':
      return Severity.GOOD;
    case 'ADVICE':
      return Severity.CAUTION;
    case 'URGENT':
      return Severity.BAD;
    default:
      return null;
  }
}

function formatSeverity(severity: Severity | null): string | null {
  switch (severity) {
    case Severity.GOOD:
      return 'NOTICE';
    case Severity.CAUTION:
      return 'ADVICE';
    case Severity.BAD:
      return 'URGENT';
    default:
      return null;
  }
}
